<template>
  <!-- Mobile table view -->
  <div v-if="isMobile && $slots['mobile-item']">
    <!-- Mobile skeleton view -->
    <slot v-if="loading" name="mobile-skeleton" />

    <!-- Mobile empty view -->
    <slot v-else-if="!items.length" name="mobile-empty" />

    <!-- Mobile item view -->
    <template v-else>
      <slot v-for="(item, index) in items" :key="item.id || index" name="mobile-item" :item="item" />
    </template>
  </div>

  <!-- Desktop table view -->
  <table v-else :class="[layout, 'text-left text-sm']" :aria-describedby="description">
    <slot name="header">
      <thead v-if="!hideDefaultHeader && columns.length" class="border-b border-gray-200">
        <tr>
          <th
            v-for="column in columns"
            :key="column.id"
            class="relative px-5 py-3 font-bold"
            :class="[{ 'sortable cursor-pointer': column.sortable }, `text-${column.align || 'left'}`, column.classes]"
            @click="
              column.sortable && sort
                ? $emit('headerClick', { column: column.id, direction: toggleSortDirection(sort.direction) })
                : null
            "
          >
            <div v-if="displayColumnsTitleUsingTranslation" class="item-title">{{ $t(column.title!) }}</div>
            <div v-else class="item-title">{{ column.title }}</div>

            <!-- Additional div for the 'channel' column -->
            <div v-if="column.id === 'channel'" class="channels">
              <span>1</span><span>2</span><span>3</span><span>4</span>
            </div>

            <!-- Conditionally render sorting icons -->
            <div
              v-if="column.sortable"
              class="absolute right-0 top-0 flex h-full w-4 flex-col items-center justify-center gap-0.5"
            >
              <VcIcon
                class="ml-1"
                :class="{
                  'text-[--color-primary]': sort && sort.column === column.id,
                  'text-[--color-neutral-a1]': !sort || sort.column !== column.id,
                }"
                :name="
                  sort && sort.column === column.id
                    ? sort.direction === SortDirection.Descending
                      ? 'chevron-down'
                      : 'chevron-up'
                    : 'chevron-down'
                "
                size="xs"
              />
            </div>
          </th>
        </tr>
      </thead>
    </slot>

    <!-- Desktop skeleton view -->
    <tbody v-if="loading">
      <slot name="desktop-skeleton" />
    </tbody>

    <!-- Desktop empty view -->
    <tbody v-else-if="!items.length">
      <slot name="desktop-empty" />
    </tbody>

    <!-- Desktop table view -->
    <tbody v-else>
      <slot name="desktop-body" />
    </tbody>
  </table>

  <!-- Table footer -->
  <slot name="footer">
    <p v-if="pageLimit && page >= pageLimit" class="mt-3 text-center">
      <slot name="page-limit-message">
        {{ $t("ui_kit.reach_limit.page_limit") }}
      </slot>
    </p>
    <VcPagination
      v-if="!hideDefaultFooter && items.length && pages > 1"
      :page="page"
      :pages="Math.min(pages, pageLimit || pages)"
      class="self-end"
      :class="[isMobile ? 'px-6 py-5' : 'mt-5 px-5 pb-5']"
      @update:page="onPageUpdate"
    />
  </slot>
</template>

<script setup lang="ts" generic="T extends ItemType">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { PAGE_LIMIT } from "@/core/constants";
import { SortDirection } from "@/core/enums";
import type { ISortInfo } from "@/core/types";

export type ItemType = {
  id?: string | number;
  [key: string]: unknown;
};

interface IEmits {
  (event: "itemClick", item: T): void;
  (event: "headerClick", item: ISortInfo): void;
  (event: "pageChanged", page: number): void;
}

interface IProps {
  columns?: ITableColumn[];
  displayColumnsTitleUsingTranslation?: boolean;
  items?: T[];
  sort?: ISortInfo;
  pages?: number;
  page?: number;
  loading?: boolean;
  hideDefaultHeader?: boolean;
  hideDefaultFooter?: boolean;
  layout?: string;
  description?: string;
  pageLimit?: number | null;
}

const emit = defineEmits<IEmits>();

withDefaults(defineProps<IProps>(), {
  columns: () => [],
  items: () => [],
  pages: 0,
  page: 0,
  layout: "w-full",
  pageLimit: PAGE_LIMIT,
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");

const onPageUpdate = (newPage: number) => {
  emit("pageChanged", newPage);
};

function toggleSortDirection(currentDirection: SortDirection): SortDirection {
  return currentDirection === SortDirection.Descending ? SortDirection.Ascending : SortDirection.Descending;
}
</script>

<style lang="scss" scoped>
.vc-pagination {
  @apply md:flex-row md:justify-end mt-0 pt-5 #{!important};

  &__result {
    @apply w-auto #{!important};
  }
}
</style>
